import React, { useState, useEffect, useRef } from "react";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import DataMappingContent from "../../components/DataMappingContent";
import { CsrAPI } from "../../apis/CsrAPI";
import { toast, ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
// import useQuestions from "../../hooks/useQuestions";
import { useTableStatusPolling } from "../../hooks/useTableStatusPolling";

const DataMapping = () => {
  const { wrongEnvironment, setAjaxLoading } = useCsr();
  const { studyId } = useParams();
  const [selections, setSelections] = useState({
    demography: [],
    diseaseCharacteristics: [],
    death: [],
    discontinuation: [],
    frequency: [],
    otherSignificant: [],
    safetySummary: [],
    serious: [],
    severity: [],
    specialInterest: [],
    treatmentRelated: [],
    protocolDeviations: [],
    primaryEfficacy: [],
    secondaryEfficacy: [],
    dsHematology: [],
    avHematology: [],
    stHematology: [],
    dsSerumChemistry: [],
    avSerumChemistry: [],
    stSerumChemistry: [],
    dsUrinalysis: [],
    avUrinalysis: [],
    stUrinalysis: [],
    dsSpecialInterest: [],
    avSpecialInterest: [],
    stSpecialInterest: [],
    dsVitalSigns: [],
    avVitalSigns: [],
    stVitalSigns: [],
    dsEcg: [],
    avEcg: [],
    stEcg: [],
    medicalHistory: [],
    diseaseMedicalHistory: [],
    concomitantTherapy: [],
    diseaseConcomitantTherapy: [],
    exposure: [],
    compliance: [],
    diseaseCompliance: [],
  });
  const errRef = useRef();
  // const { formFilledData, wizardFormFilledData } = useQuestions();
  // const [qFormSavedData, setQFormSavedData] = useState(null);
  // const [wizardFormSavedData, setWizardFormSavedData] = useState(null);

  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);

  // Use the polling hook for table status
  const { isReady: isTableStatusReady, error: tableStatusError } =
    useTableStatusPolling(studyId, 5000);

  // Shared API data state for all dropdowns
  const [sharedTableData, setSharedTableData] = useState({
    tableOptions: [],
    isDataReady: false,
    isLoadingData: false,
    dataError: null,
  });

  // Fetch shared table data once for all dropdowns
  const fetchSharedTableData = async () => {
    if (!studyId) return;

    try {
      setSharedTableData(prev => ({
        ...prev,
        isLoadingData: true,
        dataError: null,
      }));

      // Use the polling hook status instead of manual API call
      if (isTableStatusReady) {
        // 2. Fetch table names if ready
        const tablesRes = await CsrAPI.getTableNames({ studyId });
        const tableNames = [...new Set(tablesRes)] || [];

        const questionsData = await CsrAPI.getQuestions({ studyId });

        const dataMapping = questionsData.dataMapping;
        // setQFormSavedData(questionsData.qForm || null);
        // setWizardFormSavedData(questionsData.wizardForm || null);

        // Format table names for dropdown options
        const formattedTableOptions = tableNames
          .filter(item => item && typeof item === "string")
          .map(item => ({
            value: item,
            label: item,
            valueHelper: item,
          }));

        setSharedTableData({
          tableOptions: formattedTableOptions,
          isDataReady: true,
          isLoadingData: false,
          dataError: null,
        });

        // Pre-populate selections if no saved data exists
        const hasAnySavedData = Object.values(selections).some(
          selection => Array.isArray(selection) && selection.length > 0
        );

        if (!hasAnySavedData && dataMapping) {
          setSelections(prev => ({
            ...prev,
            demography: dataMapping.demography || [],
            diseaseCharacteristics: dataMapping.diseaseCharacteristics || [],
            death: dataMapping.death || [],
            discontinuation: dataMapping.discontinuation || [],
            frequency: dataMapping.frequency || [],
            otherSignificant: dataMapping.otherSignificant || [],
            safetySummary: dataMapping.safetySummary || [],
            serious: dataMapping.serious || [],
            severity: dataMapping.severity || [],
            specialInterest: dataMapping.specialInterest || [],
            treatmentRelated: dataMapping.treatmentRelated || [],
            protocolDeviations: dataMapping.protocolDeviations || [],
            primaryEfficacy: dataMapping.primaryEfficacy || [],
            secondaryEfficacy: dataMapping.secondaryEfficacy || [],
            dsHematology: dataMapping.dsHematology || [],
            avHematology: dataMapping.avHematology || [],
            stHematology: dataMapping.stHematology || [],
            dsSerumChemistry: dataMapping.dsSerumChemistry || [],
            avSerumChemistry: dataMapping.avSerumChemistry || [],
            stSerumChemistry: dataMapping.stSerumChemistry || [],
            dsUrinalysis: dataMapping.dsUrinalysis || [],
            avUrinalysis: dataMapping.avUrinalysis || [],
            stUrinalysis: dataMapping.stUrinalysis || [],
            dsSpecialInterest: dataMapping.dsSpecialInterest || [],
            avSpecialInterest: dataMapping.avSpecialInterest || [],
            stSpecialInterest: dataMapping.stSpecialInterest || [],
            dsVitalSigns: dataMapping.dsVitalSigns || [],
            avVitalSigns: dataMapping.avVitalSigns || [],
            stVitalSigns: dataMapping.stVitalSigns || [],
            dsEcg: dataMapping.dsEcg || [],
            avEcg: dataMapping.avEcg || [],
            stEcg: dataMapping.stEcg || [],
            medicalHistory: dataMapping.medicalHistory || [],
            diseaseMedicalHistory: dataMapping.diseaseMedicalHistory || [],
            concomitantTherapy: dataMapping.concomitantTherapy || [],
            diseaseConcomitantTherapy:
              dataMapping.diseaseConcomitantTherapy || [],
            exposure: dataMapping.exposure || [],
            compliance: dataMapping.compliance || [],
            diseaseCompliance: dataMapping.diseaseCompliance || [],
          }));
        }
      } else {
        setSharedTableData(prev => ({
          ...prev,
          isDataReady: false,
          isLoadingData: false,
          dataError: tableStatusError || "Table extraction not ready yet",
        }));
      }
    } catch (err) {
      console.error("Error fetching shared table data:", err);
      setSharedTableData(prev => ({
        ...prev,
        isLoadingData: false,
        dataError: "Failed to load table data. Please try again.",
      }));
    }
  };

  // Effect to fetch data when table status becomes ready
  useEffect(() => {
    if (isTableStatusReady && !sharedTableData.isDataReady) {
      fetchSharedTableData();
    }
  }, [isTableStatusReady, sharedTableData.isDataReady]);

  // Handle CSR File button click - triggers API call to save table selections
  const handleCsrFileClick = async () => {
    try {
      const state = {
        dataMapping: {
          ...selections,
        },
      };

      // Return the Promise so it can be awaited by navigation handlers
      return CsrAPI.saveDataMappingTablesSelection({ studyId, state })
        .then(() => {
          setErrMsg("");
          toast.success("Data saved successfully!");
          return Promise.resolve(); // Explicitly return resolved promise for success
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else if (error.response?.status === 403) {
            setShowRedirect(true);
          } else {
            setErrMsg("Failed to save questions");
          }
          errRef.current?.focus();
          return Promise.reject(error); // Re-throw the error for navigation handler
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    } catch (error) {
      console.error("Failed to save table selections:", error);
    }
  };

  const PageContent = () => {
    return (
      <div>
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <DataMappingContent
          selections={selections}
          setSelections={setSelections}
          sharedTableData={sharedTableData}
        />
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Please complete the Data Mapping before proceeding to CSR generation"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Comparative PD",
        buttonGoToAction: "/csr/" + studyId + "/comppharmacodynamics",
      }}
      nextButton={{
        buttonTitle: "CSR File",
        buttonGoToAction: "/csr/" + studyId + "/file",
      }}
      saveButton={true}
      saveButtonHandler={handleCsrFileClick}
    />
  );
};

export default DataMapping;
